import React, { forwardRef, useImperativeHandle, useEffect, useRef, useState, useId} from 'react';
import { PanoramaVFTConfig } from '../models/PanoramaVFTConfig';
import { isEmpty } from '../lib/Helper';

type CanvasProps = {
    globalConfig: PanoramaVFTConfig;
    rotatePanoramaCamera: (end:number) => void;
    switchPanorama: (node:any) => void;
    onCanvasLoad: (ctx:any) => void;
};

const Canvas = forwardRef(({globalConfig, rotatePanoramaCamera, switchPanorama, onCanvasLoad}: CanvasProps, ref) => {

    const [canvasContext, setCanvasContext] = useState<any>(null);

    let canvasIframe = useRef<any>();

    const InitializeCanvas = (details:any) => {
        if (!isEmpty(details) && !isEmpty(details.context)) {
            setCanvasContext(details.context);
        }
    }

    const PopulatePortals = () => {
        for (var key in canvasContext.registeredPortals) {

            for (var i = 0; i < globalConfig.vftPortals.length; i++) {
                var node = globalConfig.vftPortals[i];
                if (!isEmpty(node.eventName)) {
                    if (node.eventType === "vftPortal" && node.eventName === key) {
                        if (!isEmpty(node.degree)) {
                            canvasContext.getPortal(key).setInitialDegree(node.degree);
                        }
                    }
                }
            }
        }
    }

    const OnRadarRotated = (detail:any) => {
        var offsetRadian = globalConfig.offset * Math.PI / 180;
        rotatePanoramaCamera(detail.radian - offsetRadian);
    }

    const OnPortalClicked = (name:string) => {
        for (var i = 0; i < globalConfig.vftPortals.length; i++) {
            var childNode = globalConfig.vftPortals[i];
            if (childNode.eventType === "vftPortal" && childNode.eventName === name) {
                canvasContext.translateRadar(name);
                switchPanorama(childNode);
            }
        }
    }

    const OnCameraRotate = (viewer:any) => {
        if (canvasContext) {
            canvasContext.rotateRadar(viewer.getControl().getAzimuthalAngle() * 180 / Math.PI);
            canvasContext.scaleRadar(viewer.getCamera().fov);
        }
    }

    const OnInfoPortalClick = (key:string) => {
        OnPortalClicked(key);
    }

    const SetPortalVisited = (portals:string[]) => {
        if (canvasContext) {
            for (var i = 0; i < portals.length; i++) {
                canvasContext.getPortal(portals[i]).setVisited();
            }
        } else {
            canvasIframe.current.addEventListener('initialized', function () {
                for (var i = 0; i < portals.length; i++) {
                    canvasContext.getPortal(portals[i]).setVisited();
                }
            });
        }
    }

    useEffect(() => {
        if (Object.keys(globalConfig).length > 0) {
            if (canvasIframe && canvasIframe.current) {
                canvasIframe.current.innerHTML = '';

                const onIframeLoad = () => {
                    canvasIframe.current.contentDocument.addEventListener("Initialized", function (event:any) {
                        InitializeCanvas(event.detail);
                    });
                }
                canvasIframe.current.addEventListener('load', onIframeLoad);
            }
        }
    }, [globalConfig, canvasIframe]);

    useEffect(() => {
        if (canvasContext) {
            canvasIframe.current.contentDocument.addEventListener("PortalClicked", function (event:any) {
                OnPortalClicked(event.detail);
            });

            canvasIframe.current.contentDocument.addEventListener("RadarRotated", function (event:any) {
                OnRadarRotated(event.detail);
            });

            PopulatePortals();

            canvasContext.attachRadarListeners(globalConfig.offset);

            if (!isEmpty(globalConfig.onloadPortal)) {
                canvasContext.translateRadar(globalConfig.onloadPortal);
            }

            canvasIframe.current.dispatchEvent(new CustomEvent("initialized"));
            onCanvasLoad(canvasContext);
        }
        /**
         * Since the functions will also be referenced outside of the useEffect scope and theres no point rerendering everytime the function dependencies change, 
         * it is simpler to just disable the lint.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvasContext]); 

    useImperativeHandle(ref, () => ({
        canvasIframe,
        OnCameraRotate,
        OnInfoPortalClick,
        SetPortalVisited
    }));

    return (
        <iframe className="vft-plan-view" ref={canvasIframe} title={useId()} src={globalConfig.canvasSrc} width="100%" height="100%"></iframe>
    );
});

export default Canvas;